import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nProvider";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Lock, Unlock, ShieldCheck, History } from "lucide-react";

const ROLES = ["super_admin", "operatore", "cliente"];

const ROLE_BADGE = {
  super_admin: "bg-rose-100 text-rose-800 border-rose-200",
  operatore: "bg-sky-100 text-sky-800 border-sky-200",
  cliente: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

export default function UsersPage() {
  const { t } = useI18n();
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === "super_admin";

  const [list, setList] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    email: "",
    name: "",
    role: "operatore",
    password: "",
    company_id: "",
  });

  // Edit dialog
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "operatore", company_id: "", password: "" });

  // Delete confirmation
  const [deleteUser, setDeleteUser] = useState(null);

  // Audit log
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const openAudit = async () => {
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const r = await api.get("/users/audit?limit=200");
      setAuditRows(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento audit");
    }
    setAuditLoading(false);
  };
  const ACTION_LABEL = {
    create: { label: "Creazione", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    update: { label: "Modifica", cls: "bg-slate-100 text-slate-800 border-slate-200" },
    role_change: { label: "Cambio ruolo", cls: "bg-amber-100 text-amber-900 border-amber-200" },
    activation_change: { label: "Attivazione", cls: "bg-sky-100 text-sky-800 border-sky-200" },
    password_change: { label: "Password", cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
    delete: { label: "Eliminazione", cls: "bg-rose-100 text-rose-800 border-rose-200" },
  };

  const load = () => api.get("/users").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/companies").then((r) => setCompanies(Array.isArray(r.data) ? r.data : r.data?.items || []));
  }, []);

  const companyName = useMemo(() => {
    const m = new Map();
    companies.forEach((c) => m.set(c.id, c.name));
    return (id) => (id ? m.get(id) || "—" : "—");
  }, [companies]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", { ...form, company_id: form.company_id || null });
      setCreateOpen(false);
      setForm({ email: "", name: "", role: "operatore", password: "", company_id: "" });
      toast.success(t("save"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      name: u.name || "",
      role: u.role,
      company_id: u.company_id || "",
      password: "",
    });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    try {
      const payload = {
        name: editForm.name,
        role: editForm.role,
        company_id: editForm.role === "cliente" ? editForm.company_id || null : null,
      };
      if (editForm.password && editForm.password.length > 0) {
        payload.password = editForm.password;
      }
      await api.patch(`/users/${editUser.id}`, payload);
      toast.success("Utente aggiornato");
      setEditUser(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento");
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Utente bloccato" : "Utente sbloccato");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const confirmDelete = async () => {
    if (!deleteUser) return;
    try {
      await api.delete(`/users/${deleteUser.id}`);
      toast.success("Utente eliminato");
      setDeleteUser(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
      setDeleteUser(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("users")}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {list.length} utent{list.length === 1 ? "e" : "i"}
          </p>
        </div>
        {isSuperAdmin && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={openAudit} className="rounded-full btn-lift" data-testid="users-audit-btn">
              <History className="w-4 h-4 mr-1.5" /> Audit log
            </Button>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full btn-lift" data-testid="users-add-button">
                <Plus className="w-4 h-4 mr-1.5" /> {t("add_user")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("add_user")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>{t("email")}</Label>
                  <Input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="users-create-email"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Nome</Label>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="users-create-name"
                  />
                </div>
                <div>
                  <Label>{t("password")}</Label>
                  <Input
                    type="password"
                    required
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="users-create-password"
                  />
                </div>
                <div>
                  <Label>Ruolo</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="users-create-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r}>
                          {t(`role_${r}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {form.role === "cliente" && (
                  <div className="col-span-2">
                    <Label>{t("company")}</Label>
                    <Select
                      value={form.company_id}
                      onValueChange={(v) => setForm({ ...form, company_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <DialogFooter className="col-span-2">
                  <Button type="submit" className="rounded-full btn-lift" data-testid="users-create-submit">
                    {t("save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-5 py-3">{t("email")}</th>
              <th className="text-left px-5 py-3">Nome</th>
              <th className="text-left px-5 py-3">Ruolo</th>
              <th className="text-left px-5 py-3">Azienda</th>
              <th className="text-left px-5 py-3">{t("active")}</th>
              {isSuperAdmin && <th className="text-right px-5 py-3">Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {list.map((u) => {
              const isSelf = u.id === me?.id;
              return (
                <tr
                  key={u.id}
                  className="border-t border-border hover:bg-muted/40"
                  data-testid={`users-row-${u.id}`}
                >
                  <td className="px-5 py-3 font-medium">
                    {u.email}
                    {isSelf && (
                      <span className="ml-2 text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5">
                        Tu
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3">{u.name}</td>
                  <td className="px-5 py-3">
                    <span
                      className={`text-[11px] uppercase tracking-widest px-2 py-0.5 rounded border ${
                        ROLE_BADGE[u.role] || "bg-slate-100 text-slate-700 border-slate-200"
                      }`}
                    >
                      {t(`role_${u.role}`) || u.role}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-muted-foreground">
                    {u.role === "cliente" ? companyName(u.company_id) : "—"}
                  </td>
                  <td className="px-5 py-3">
                    {u.is_active ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium">
                        <ShieldCheck className="w-3.5 h-3.5" /> Attivo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-rose-700 font-medium">
                        <Lock className="w-3.5 h-3.5" /> Bloccato
                      </span>
                    )}
                  </td>
                  {isSuperAdmin && (
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full h-8 px-3"
                          onClick={() => openEdit(u)}
                          data-testid={`users-edit-${u.id}`}
                          title="Modifica ruolo / nome / password"
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1" /> Modifica
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className={`rounded-full h-8 px-3 ${
                            u.is_active
                              ? "text-amber-700 border-amber-200 hover:bg-amber-50"
                              : "text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                          }`}
                          disabled={isSelf}
                          onClick={() => toggleActive(u)}
                          data-testid={`users-toggle-${u.id}`}
                          title={u.is_active ? "Blocca temporaneamente l'accesso" : "Riattiva l'accesso"}
                        >
                          {u.is_active ? (
                            <>
                              <Lock className="w-3.5 h-3.5 mr-1" /> Blocca
                            </>
                          ) : (
                            <>
                              <Unlock className="w-3.5 h-3.5 mr-1" /> Sblocca
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full h-8 px-3 text-rose-700 border-rose-200 hover:bg-rose-50"
                          disabled={isSelf}
                          onClick={() => setDeleteUser(u)}
                          data-testid={`users-delete-${u.id}`}
                          title="Elimina definitivamente l'utente"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
            {list.length === 0 && (
              <tr>
                <td colSpan={isSuperAdmin ? 6 : 5} className="px-5 py-8 text-center text-muted-foreground italic">
                  Nessun utente
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit user dialog */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica utente</DialogTitle>
            <DialogDescription>
              {editUser?.email} — puoi cambiare ruolo, nome o reimpostare la password.
            </DialogDescription>
          </DialogHeader>
          {editUser && (
            <form onSubmit={submitEdit} className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nome</Label>
                <Input
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  data-testid="users-edit-name"
                />
              </div>
              <div className="col-span-2">
                <Label>Ruolo</Label>
                <Select
                  value={editForm.role}
                  onValueChange={(v) => setEditForm({ ...editForm, role: v })}
                  disabled={editUser.id === me?.id}
                >
                  <SelectTrigger data-testid="users-edit-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {t(`role_${r}`) || r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editUser.id === me?.id && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Non puoi modificare il tuo stesso ruolo.
                  </p>
                )}
              </div>
              {editForm.role === "cliente" && (
                <div className="col-span-2">
                  <Label>{t("company")}</Label>
                  <Select
                    value={editForm.company_id}
                    onValueChange={(v) => setEditForm({ ...editForm, company_id: v })}
                  >
                    <SelectTrigger data-testid="users-edit-company">
                      <SelectValue placeholder="Seleziona azienda…" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-2">
                <Label>Nuova password (opzionale)</Label>
                <Input
                  type="password"
                  placeholder="Lascia vuoto per non modificare"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  data-testid="users-edit-password"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Se compilata, la password sarà sovrascritta.
                </p>
              </div>
              <DialogFooter className="col-span-2">
                <Button type="button" variant="outline" className="rounded-full" onClick={() => setEditUser(null)}>
                  Annulla
                </Button>
                <Button type="submit" className="rounded-full btn-lift" data-testid="users-edit-submit">
                  Salva modifiche
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteUser} onOpenChange={(o) => !o && setDeleteUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare definitivamente questo utente?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare <b>{deleteUser?.email}</b> ({deleteUser?.name}). L'operazione è
              <b> irreversibile</b> e l'utente non potrà più accedere. Se vuoi solo impedirgli
              temporaneamente l'accesso, usa il pulsante <b>Blocca</b>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="users-delete-confirm"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Audit log dialog */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registro attività utenti</DialogTitle>
            <DialogDescription>
              Cronologia immutabile di creazione, modifica, cambio ruolo, attivazione ed eliminazione degli utenti. Ordinata dalla più recente.
            </DialogDescription>
          </DialogHeader>
          {auditLoading ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Caricamento…</div>
          ) : auditRows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Nessuna attività registrata.</div>
          ) : (
            <div className="border border-border rounded-md overflow-hidden" data-testid="users-audit-table">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-[10px] uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2">Quando</th>
                    <th className="text-left px-3 py-2">Azione</th>
                    <th className="text-left px-3 py-2">Utente</th>
                    <th className="text-left px-3 py-2">Ruolo</th>
                    <th className="text-left px-3 py-2">Eseguito da</th>
                    <th className="text-left px-3 py-2">Dettagli</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {auditRows.map((r) => {
                    const meta = ACTION_LABEL[r.action] || { label: r.action, cls: "bg-slate-100 text-slate-800 border-slate-200" };
                    const diff = r.details?.diff || {};
                    const diffKeys = Object.keys(diff);
                    return (
                      <tr key={r.id} className="hover:bg-muted/30">
                        <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                          {new Date(r.at).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${meta.cls}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-medium">{r.target_email || "—"}</td>
                        <td className="px-3 py-2 text-muted-foreground">{r.target_role || "—"}</td>
                        <td className="px-3 py-2 text-muted-foreground">{r.actor_email || "—"}</td>
                        <td className="px-3 py-2 text-[11px] text-muted-foreground">
                          {diffKeys.length > 0 ? (
                            diffKeys.map((k) => (
                              <div key={k}><b>{k}</b>: {typeof diff[k] === "object" ? `${JSON.stringify(diff[k].from)} → ${JSON.stringify(diff[k].to)}` : String(diff[k])}</div>
                            ))
                          ) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
